import { useReCaptcha } from '@/core/recaptcha/useReCaptcha';
import { usePriceService } from '@/layout/appWrapper/ServiceProvider';
import { useLocalization } from '@/services/localizationService';
import { FREE_TRIAL_DAYS, STRIPE_APPEARANCE } from '@/services/paymentService';
import { useUser } from '@/services/userService';
import {
  isFreeTrialEnabledState,
  isReminderEnabledState,
  selectedPeriodState,
} from '@/stores/onboardingStore';
import settingsState from '@/stores/settingsStore';
import Spinner from '@/ui/content/Spinner';
import React, { useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useStripeProvider } from '@/core/stripe/useStripeProvider';
import { FreeTrialButton } from '.';
import PaymentInformationForm from './PaymentInformationForm';

export default function PaymentForm({ onSubmit }: { onSubmit: () => void }) {
  const settings = useRecoilValue(settingsState);
  const { useFormatRecurringPrice, usePaywallPrices } = usePriceService();
  const { stripePromise } = useStripeProvider();

  const { data: user } = useUser();

  const [isFreeTrialEnabled, setIsFreeTrialEnabled] = useRecoilState(
    isFreeTrialEnabledState
  );
  const [isReminderEnabled, setIsReminderEnabled] = useRecoilState(
    isReminderEnabledState
  );

  const period = 'weekly';

  const [selectedPeriod, setSelectedPeriod] =
    useRecoilState(selectedPeriodState);
  const { t, data } = useLocalization();

  const { disableEmailReminder } = settings.values;

  const paywallPrices = usePaywallPrices();

  const isFreeTrialAvailable =
    user?.monetization?.trialAvailable && settings.values.isFreeTrialEnabled;

  const selectedPrice = paywallPrices?.base[selectedPeriod];
  const formattedPrice = useFormatRecurringPrice(selectedPrice);

  useReCaptcha();

  useEffect(() => {
    setSelectedPeriod(period);
  }, [period, setSelectedPeriod]);

  if (!paywallPrices || !selectedPrice) return <Spinner inline={false} />;

  const options: StripeElementsOptions = {
    mode: 'setup',
    currency: selectedPrice.currency,
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        {isFreeTrialAvailable ? (
          <h2 className="SB25">
            {t(data.onboarding.activateYour)}{' '}
            <span className="text-primary-accent">
              {t(data.common.freeTrial, null, 'lowercase')}
            </span>
            <br />
            {t(data.onboarding.andGetStarted)} 🎉
          </h2>
        ) : (
          <h2 className="SB25">{t(data.onboarding.getStarted)} 🎉</h2>
        )}
      </div>
      {isFreeTrialAvailable && (
        <FreeTrialButton
          isFreeTrialEnabled={isFreeTrialEnabled}
          setIsFreeTrialEnabled={setIsFreeTrialEnabled}
          isReminderEnabled={isReminderEnabled}
          setIsReminderEnabled={setIsReminderEnabled}
          showReminder
        />
      )}
      {isFreeTrialAvailable ? (
        <p
          className={`M18 text-center pb-2 ${
            !disableEmailReminder ? '!mt-3' : ''
          }`}
        >
          {isFreeTrialEnabled && (
            <span>
              {t(data.onboarding.daysFree, { n: FREE_TRIAL_DAYS })},{' '}
              {t(data.common.then, null, 'lowercase')}{' '}
            </span>
          )}
          {formattedPrice}
        </p>
      ) : (
        <p className="M18 text-center pb-2">{formattedPrice}</p>
      )}

      <Elements
        stripe={stripePromise}
        options={{
          ...options,
          appearance: STRIPE_APPEARANCE,
        }}
      >
        <PaymentInformationForm
          selectedPrice={selectedPrice}
          onSubmit={onSubmit}
          willDownloadAutomatically={false}
        />
      </Elements>
    </div>
  );
}
